import { PlusIcon } from "@xola/icons";
import { flash, PopoverList } from "@xola/ui-kit";
import { sortBy } from "lodash";
import React, { useMemo, useRef, useState } from "react";
import { LineItemTemplate, PurchaseRule, PurchaseRuleLink } from "types";
import { PromptModal } from "@/components/Common/PromptModal";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import {
    addMerchandiseToPurchaseRule,
    isMerchandiseDisabledForPurchaseRule,
} from "@/components/Settings/TaxesAndFees/TaxAndFeeFormHelper";
import { getSelectedItems } from "@/components/Settings/TaxesAndFees/TaxAndFeeFormValuesHelper";
import { TaxAndFeeList } from "@/components/Settings/TaxesAndFees/TaxAndFeeList";
import { useProducts } from "@/hooks/resources/useProduct";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCollection } from "@/hooks/useCollection";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useModal, useVisibility } from "@/hooks/useVisibility";
import { getSeller } from "@/utils/getSeller";

interface MerchandiseTaxesAndFeesProps {
    merchandiseId: ID;
    onManage: () => void;
    onEditTaxPurchaseRule: (purchaseRule: PurchaseRule, purchaseRuleLinkId?: ID) => void;
}

export const MerchandiseTaxesAndFees = ({
    merchandiseId,
    onManage,
    onEditTaxPurchaseRule,
}: MerchandiseTaxesAndFeesProps) => {
    const [isPopoverVisible, { show: showPopover, hide: hidePopover }] = useVisibility(false);
    const confirmModal = useModal();
    const selectedPurchaseRuleRef = useRef<PurchaseRule | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const { data: products } = useProducts();

    const { data: purchaseRules, refetch: refetchPurchaseRules } = useInfiniteCollection<PurchaseRule>(
        "purchaseRules",
        { "tags._id[in]": "fee,tax" },
    );

    const purchaseRuleIds = purchaseRules?.map((purchaseRule) => purchaseRule.id) ?? [];

    const { data: purchaseRuleLinks, refetch: refetchPurchaseRuleLinks } = useCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        { "purchaseRules.purchaseRulePermanentId[in]": purchaseRuleIds.join(",") },
        { enabled: purchaseRuleIds.length > 0 },
    );

    // O(1) lookup map: purchaseRule.id -> PurchaseRuleLink
    const purchaseRuleLinkMap = useMemo(() => {
        const map = new Map<ID, PurchaseRuleLink>();
        for (const link of purchaseRuleLinks ?? []) {
            for (const rule of link.purchaseRules) {
                if (rule.purchaseRulePermanentId) {
                    map.set(rule.purchaseRulePermanentId, link);
                }
            }
        }
        return map;
    }, [purchaseRuleLinks]);

    const getPurchaseRuleLink = (purchaseRule: PurchaseRule) =>
        purchaseRule.id ? purchaseRuleLinkMap.get(purchaseRule.id) : undefined;

    // Table list: show rules that are currently applied to this merchandise.
    const handleFilterPurchaseRules = (purchaseRule: PurchaseRule, purchaseRuleLink?: PurchaseRuleLink) => {
        // Hide rules whose terms are structurally incompatible with merchandise.
        if (isMerchandiseDisabledForPurchaseRule(purchaseRule)) {
            return false;
        }

        const { isAllSelected, selectedMerchandiseIds } = getSelectedItems(purchaseRule, purchaseRuleLink, products);

        if (isAllSelected.merchandise) {
            return true;
        }

        return selectedMerchandiseIds.includes(merchandiseId);
    };

    // Popover list: show merchandise-compatible rules that can be additionally applied to this merchandise.
    const handlePopoverFilterPurchaseRules = (purchaseRule: PurchaseRule, purchaseRuleLink?: PurchaseRuleLink) => {
        // Exclude rules whose terms are structurally incompatible with merchandise (e.g. Per Person, Per Add-on)
        if (isMerchandiseDisabledForPurchaseRule(purchaseRule)) {
            return false;
        }

        const { isAllSelected, selectedMerchandiseIds } = getSelectedItems(purchaseRule, purchaseRuleLink, products);

        // Already applies to all merchandise — no need to show in "add" popover
        if (isAllSelected.merchandise) {
            return false;
        }

        // Already applied to this specific merchandise
        if (selectedMerchandiseIds.includes(merchandiseId)) {
            return false;
        }

        return true;
    };

    const typedPurchaseRules: PurchaseRule[] = purchaseRules ?? [];

    const popoverPurchaseRules: PurchaseRule[] = sortBy(
        typedPurchaseRules.filter((purchaseRule) => {
            return handlePopoverFilterPurchaseRules(purchaseRule, getPurchaseRuleLink(purchaseRule));
        }),
        "createdAt",
    );

    const mappedPurchaseRuleTemplateIds = popoverPurchaseRules
        .map((purchaseRule) => purchaseRule.actions[0]?.template?.id)
        .filter(Boolean)
        .join(",");

    const { data: lineItemTemplates = [] } = useAutoFetchCollection<LineItemTemplate>(
        "lineItemTemplates",
        {
            "id[in]": mappedPurchaseRuleTemplateIds,
        },
        { enabled: mappedPurchaseRuleTemplateIds.length > 0 },
    );

    const getPopoverLabel = (purchaseRule: PurchaseRule) => {
        const lineItemTemplateId = purchaseRule.actions[0]?.template?.id;

        if (!lineItemTemplateId) {
            return purchaseRule.id;
        }

        const template = lineItemTemplates.find((lineItemTemplate) => lineItemTemplate.id === lineItemTemplateId);
        return template?.name ?? purchaseRule.id;
    };

    const handleCreateNewTaxAndFee = () => {
        hidePopover();
        onManage();
    };

    const handleEditTaxAndFee = (purchaseRule: PurchaseRule) => {
        hidePopover();
        selectedPurchaseRuleRef.current = purchaseRule;
        confirmModal.show();
    };

    const handleConfirmApply = async () => {
        const purchaseRule = selectedPurchaseRuleRef.current;
        if (!purchaseRule) {
            return;
        }

        const purchaseRuleLink = getPurchaseRuleLink(purchaseRule);
        if (!purchaseRuleLink) {
            flash.error("Unable to apply tax / fee. Please try again.");
            confirmModal.hide();
            selectedPurchaseRuleRef.current = null;
            return;
        }

        setIsSaving(true);
        try {
            const seller = getSeller();
            await addMerchandiseToPurchaseRule(purchaseRule, purchaseRuleLink, merchandiseId, seller.id);
            await Promise.all([refetchPurchaseRules(), refetchPurchaseRuleLinks()]);
            flash.success("Tax / Fee applied to this merchandise");
        } catch {
            flash.error("Failed to apply tax / fee");
        } finally {
            setIsSaving(false);
            confirmModal.hide();
            selectedPurchaseRuleRef.current = null;
        }
    };

    return (
        <div>
            <div className="mb-4 flex items-center justify-between">
                <h4 className="text-md font-bold">Taxes & Fees</h4>
                <PopoverList
                    visible={isPopoverVisible}
                    className="max-h-96 w-75 overflow-y-scroll"
                    onClickOutside={hidePopover}
                >
                    <SecondaryButton
                        size="small"
                        icon={<PlusIcon />}
                        onClick={isPopoverVisible ? hidePopover : showPopover}
                    >
                        Taxes & Fees
                    </SecondaryButton>

                    {popoverPurchaseRules.map((purchaseRule) => (
                        <PopoverList.Item
                            key={purchaseRule.id}
                            name={purchaseRule.id}
                            onClickItem={() => handleEditTaxAndFee(purchaseRule)}
                        >
                            {getPopoverLabel(purchaseRule)}
                        </PopoverList.Item>
                    ))}

                    <PopoverList.Item
                        name="newTaxAndFee"
                        className="sticky bottom-0 bg-white"
                        onClickItem={handleCreateNewTaxAndFee}
                    >
                        <SecondaryButton className="w-full" icon={<PlusIcon />}>
                            New Taxes & Fees
                        </SecondaryButton>
                    </PopoverList.Item>
                </PopoverList>
            </div>

            <div className="overflow-hidden rounded-lg">
                <TaxAndFeeList
                    hideDeleteAction
                    filterPurchaseRules={handleFilterPurchaseRules}
                    showDemographicsAndAddOns={false}
                    onEditTaxPurchaseRule={onEditTaxPurchaseRule}
                />
            </div>

            {confirmModal.isVisible && selectedPurchaseRuleRef.current && (
                <PromptModal
                    isOpen
                    title="Apply Tax / Fee"
                    color="primary"
                    isLoading={isSaving}
                    dialogText={{ secondary: "Cancel", submit: "Apply" }}
                    onConfirm={handleConfirmApply}
                    onClose={confirmModal.hide}
                >
                    Apply <var data-var="name">{getPopoverLabel(selectedPurchaseRuleRef.current)}</var> to this
                    merchandise?
                </PromptModal>
            )}
        </div>
    );
};
